"use client";

import dynamic from "next/dynamic";
import { useLocale } from "next-intl";
import type { Locale } from "@/i18n/routing";
import type { SectionsContent } from "@/content/sections";
import { t } from "@/lib/content";
import { Reveal } from "@/components/ui/reveal";

const EuropaGlobe = dynamic(
  () => import("@/components/three/EuropaGlobe").then((m) => m.EuropaGlobe),
  {
    ssr: false,
    loading: () => <GlobeSkeleton />,
  },
);

function GlobeSkeleton() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative w-[70%] aspect-square">
        <div className="absolute inset-0 rounded-full border border-line-200" />
        <div className="absolute inset-[12%] rounded-full border border-line-200/60" />
        <div className="absolute inset-[24%] rounded-full border border-line-200/40 animate-pulse" />
      </div>
    </div>
  );
}

const ownLocations = {
  de: "eigene Standorte",
  en: "own locations",
  fr: "sites en propre",
  it: "sedi proprie",
  nl: "eigen vestigingen",
  pl: "własnych lokalizacji",
  es: "sedes propias",
  cs: "vlastních poboček",
  zh: "自有站点",
  da: "egne lokationer",
};

export function EuropeMap({
  europeSection,
  factsCopy,
}: {
  europeSection: SectionsContent["europeSection"];
  factsCopy: SectionsContent["factsCopy"];
}) {
  const locale = useLocale() as Locale;
  const stats = [
    { value: "28", label: t(factsCopy.serviceArea, locale) as string },
    { value: "6", label: (ownLocations as Record<string, string>)[locale] ?? ownLocations.de },
    { value: "100.000+", label: t(factsCopy.shipmentsPerYear, locale) as string },
    { value: "24/7", label: t(factsCopy.humanDispatch, locale) as string },
  ];

  return (
    <section className="section">
      <div className="container grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:items-center">
        <div className="max-w-xl">
          <p className="kicker">{t(europeSection.kicker, locale) as string}</p>
          <h2 className="mt-5 text-[1.75rem] leading-[1.08] tracking-[-0.02em] md:text-[2.25rem] lg:text-[2.5rem] xl:text-[3rem]">
            {t(europeSection.title, locale) as string}
          </h2>
          <p className="mt-6 max-w-[58ch] text-base text-ink-700 md:text-lg leading-relaxed">
            {t(europeSection.body, locale) as string}
          </p>
          {/* Decorative stat block — switched from <dl><div><dt/><dd></div></dl>
              to plain divs because axe-core flags any non-dt/dd element as a
              direct child of <dl>. The pairs aren't real definitions either. */}
          <div className="mt-12 grid grid-cols-2 gap-x-10 gap-y-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 70}>
                <div className="border-t border-line-200 pt-4">
                  <p className="font-display text-2xl md:text-[1.625rem] font-semibold text-ink-900 tabnums">
                    {s.value}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-[0.08em] text-ink-500">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative aspect-square lg:aspect-auto lg:h-[min(620px,72vh)]">
          <EuropaGlobe />
        </div>
      </div>
    </section>
  );
}
